import { IUser } from '@featbit/react-client-sdk'
import { UserData } from 'src/State/User/UserModel'
import { UserLocationDto } from 'src/Libs/user/api/dto/user.dto'
import { FEATURE_FLAG_AUTH_TYPE, FEATURE_FLAG_E3DC_USER_BRAND_TYPE, FEATURE_FLAG_USER_PREFIX } from '../consts'

/**
 * Anonymous user to access feature flags when not logged in.
 * note: For such feature flags custom targeting rules won't work as there is no user context.
 */
export const anonymousE3dcFeatBitUser: IUser = {
  keyId: `${FEATURE_FLAG_USER_PREFIX}:anonymous`,
  name: 'anonymous',
  customizedProperties: [
    {
      name: 'authType',
      value: FEATURE_FLAG_AUTH_TYPE,
    },
    {
      name: 'brandType',
      value: FEATURE_FLAG_E3DC_USER_BRAND_TYPE,
    },
  ],
}

export interface ResolveFeatBitUserContextInput {
  isNotLoggedIn: boolean
  hasUserData: boolean
  userId: number | undefined
  user: UserData | undefined
  userContactData: UserLocationDto | undefined
  isUserAnOwner: boolean | undefined
  isFlowTheme: boolean
  previousIdentity: string | undefined
  previousUser: IUser | undefined
}

export interface ResolveFeatBitUserContextResult {
  identity: string | undefined
  user: IUser | undefined
}

/**
 * Resolves the FeatBit user context (anonymous, authenticated, or unchanged) from the current
 * auth/user state. While the identity cannot be determined (e.g. transient loading/refetch) or is
 * unchanged, the previously resolved user is returned so the FeatBit client is not re-initialized
 * with a new user unnecessarily.
 */
export const resolveFeatBitUserContext = ({
  isNotLoggedIn,
  hasUserData,
  userId,
  user,
  userContactData,
  isUserAnOwner,
  isFlowTheme,
  previousIdentity,
  previousUser,
}: ResolveFeatBitUserContextInput): ResolveFeatBitUserContextResult => {
  let identity: string | undefined
  if (isNotLoggedIn) identity = 'anonymous'
  else if (hasUserData) identity = `${FEATURE_FLAG_USER_PREFIX}:${userId}`

  if ((identity === undefined || identity === previousIdentity) && previousUser) {
    return { identity: previousIdentity, user: previousUser }
  }

  if (isNotLoggedIn) {
    return { identity, user: anonymousE3dcFeatBitUser }
  }

  if (hasUserData && user && userContactData) {
    const featBitUser: IUser = {
      keyId: `${FEATURE_FLAG_USER_PREFIX}:${userId}`,
      name: user.username,
      customizedProperties: [
        {
          name: 'authType',
          value: FEATURE_FLAG_AUTH_TYPE,
        },
        {
          name: 'ssoId',
          value: user.ssoId || '',
        },
        {
          name: 'level',
          value: user.level,
        },
        {
          name: 'companyId',
          value: user.Firmen_ID === undefined ? '' : String(user.Firmen_ID),
        },
        {
          name: 'installerCertified',
          value: String(user.installerCertified),
        },
        {
          name: 'brandType',
          value: FEATURE_FLAG_E3DC_USER_BRAND_TYPE,
        },
        {
          name: 'location',
          value: userContactData.Land ?? '',
        },
        {
          name: 'ownsAnInstallation',
          value: isUserAnOwner === undefined ? '' : String(isUserAnOwner),
        },
        {
          name: 'appTheme',
          value: isFlowTheme ? 'flow' : 'e3dc',
        },
      ],
    }
    return { identity, user: featBitUser }
  }

  return { identity: previousIdentity, user: previousUser }
}
